import { get } from '../../utils/httpRequest';
import { shouldSkipTracking } from '../utils/cookie';
import { hashValue } from '../utils/strings';
import { datalayerGlobal } from './datalayerMap';
import { datalayerStorageClient } from './datalayerStorage';
import { emptyDatalayer } from './emptyDatalayer';
import { handleTmsDataError, tmsEventDataContainsErrors } from './error';
import type {
  Datalayer,
  DatalayerEventPayload,
  DatalayerGlobal,
  DatalayerUpdater,
  TMSFetchOptions,
  TrackTMSPageDatalayer,
} from './types';
import { flatten, isNilOrEmpty } from './utils';

const {
  getDatalayerGlobalParams,
  getPreviousDatalayer,
  storePreviousDatalayer,
  updateDatalayerGlobalParams,
  getClientId,
} = datalayerStorageClient();

let lastTrackedPage: string;

/**
 * Resets the last tracked page. Primarily intended for testing purposes.
 * @param {string} [resetValue] - Optional value to reset the last tracked page to.
 * If omitted, the last tracked page will be reset to `undefined`.
 * @remarks This function is for testing only and should not be used in production code.
 */
export const resetLastTrackedTMSPage = (resetValue?: string) => {
  lastTrackedPage = resetValue;
};

/**
 * Initializes the inner TC vars.
 * When it's not available, it tries to get it from the opener tab (if available).
 * @returns {Partial<Datalayer>} - The inner TC vars.
 */
const initInnerTcVars = (): Partial<Datalayer> => {
  try {
    const openerTcVars = window?.opener?.tc_vars;
    if (openerTcVars && Object.keys(openerTcVars).length) {
      return openerTcVars;
    }
  } catch {
    // Ignore CORS error
  }

  const currentTcVars = window?.tc_vars;
  return currentTcVars && Object.keys(currentTcVars).length
    ? { ...emptyDatalayer, ...currentTcVars }
    : emptyDatalayer;
};

/**
 * Retrieves the previous TC vars from session storage or initializes them if not available.
 * If the TC vars are not found in session storage, it attempts to initialize them from the opener tab or window.tc_vars.
 * Updates the TC vars history in session storage if initialized.
 * @returns {Partial<Datalayer>} - The previous TC vars.
 */
const getPrevTCVars = (): Partial<Datalayer> => {
  let tcVars: Partial<Datalayer> = getPreviousDatalayer();
  if (!tcVars || !Object.keys(tcVars).length) {
    tcVars = initInnerTcVars();
    storePreviousDatalayer(tcVars);
    return tcVars;
  }
  const currentTCVars = window?.tc_vars || {};
  return {
    ...tcVars,
    ...Object.fromEntries(
      Object.entries(currentTCVars).filter(
        ([, value]) => !isNilOrEmpty(value as string)
      )
    ),
  };
};

/**
 * Creates a datalayer updater object.
 * @param {DatalayerUpdater} parser - The parser function to transform the input item.
 * @returns {Object} - The datalayer updater object.
 * @deprecated In product libraries, use a custom hook from frontend-core
 */
export const createDatalayerUpdater = (parser: DatalayerUpdater) => ({
  /**
   * Updates the datalayer with the provided item.
   * @param {unknown} item - The item to update the datalayer with.
   */
  update(item) {
    const datalayer: Partial<Datalayer> = {
      ...parser(item),
    };
    updateAndSyncDatalayer(datalayer);
  },
});

/**
 * Updates the session-filtered datalayer and synchronizes it with Tag Commander's variables.
 * @param datalayer - the object containing the properties to update.
 */
export const updateAndSyncDatalayer = (datalayer: Partial<Datalayer>) => {
  updateSessionFilteredDatalayer(datalayer);
  syncTcVars(datalayer);
};

/**
 * Removes keys related to product that don't need to be persisted
 * @param datalayer
 */
const updateSessionFilteredDatalayer = (datalayer: Partial<Datalayer> = {}) => {
  const filtered = filterDatalayer(datalayer);
  if (Object.keys(filtered).length) {
    updateDatalayerGlobalParams(filtered);
  }
};

/**
 * Filters the datalayer to keep only the global properties.
 * This is used to keep the datalayer clean and avoid sending unnecessary data.
 * It will remove the properties that are not global and are empty.
 * @param datalayer - the object containing the properties to update.
 * @returns {Partial<DatalayerGlobal>} - the filtered datalayer.
 */
const filterDatalayer = (
  datalayer: Partial<Datalayer> = {}
): Partial<DatalayerGlobal> => {
  return Object.keys(datalayer).reduce((acc, key) => {
    if (isTMSVariableGlobal(key) && !isNilOrEmpty(datalayer[key])) {
      acc[key] = datalayer[key];
    }
    return acc;
  }, {});
};

/**
 * Fires a datalayer event with the provided data payload.
 * @param {DatalayerEventPayload} data - The data payload for the event.
 * @deprecated In product libraries, use a custom hook from frontend-core
 */
export const fireDataLayerEvent = async (
  data: DatalayerEventPayload
): Promise<void> => {
  await fireEvent(data);
};

/**
 * Check if a variable is defined as global so it should be kept when
 * the product or the search changes
 * @param name
 * @returns {boolean}
 */
export const isTMSVariableGlobal = (name: string): boolean => {
  return datalayerGlobal.includes(name);
};

/**
 * Checks if the funnel has changed or if the user has exited the funnel.
 * @param prevDatalayer - The previous data layer state.
 * @param tcVars - The current data layer state.
 * @returns {boolean} - True if the funnel has changed or the user has exited the funnel, otherwise false.
 */
const funnelChanged = (
  prevDatalayer: Partial<Datalayer>,
  tcVars: Partial<Datalayer>
) => {
  if (!prevDatalayer || !Object.keys(prevDatalayer).length) {
    return false;
  }
  const { product } = tcVars;
  return (
    product !== undefined &&
    prevDatalayer.product &&
    prevDatalayer.product !== product
  );
};

/**
 * Retrieves client-side attributes to be included in the data layer.
 *
 * @returns {Partial<Datalayer>} An object containing partial data layer attributes:
 * - `ext_ga_client_id`: The Google Analytics client ID retrieved via `getClientId()`.
 * - `page_url`: The current page's URL from `document.location.href`.
 */
const getClientSideAttributes = (): Partial<Datalayer> => {
  return {
    ext_ga_client_id: getClientId(),
    page_url: document.location.href,
  };
};

/**
 * Syncs the datalayer with the TC variables.
 * It will keep the previous page name if the funnel has not changed.
 * It will keep the previous value if the variable is not global and the funnel has changed.
 * It will add client side attributes to the datalayer.
 * It will update the window.tc_vars with the new datalayer.
 * It will update the datalayer in the storage.
 */
export const syncTcVars = (tcVars: Partial<Datalayer> = {}) => {
  const prevDatalayer = getPrevTCVars();
  if (tcVars.page_name && prevDatalayer.page_name !== tcVars.page_name) {
    tcVars.page_previous = prevDatalayer.page_name;
  }
  const storedDatalayer = getDatalayerGlobalParams();
  const hasFunnelChanged = funnelChanged(prevDatalayer, tcVars);
  const syncFn = (key, oldValue, newValue) =>
    newValue ||
    (!hasFunnelChanged || isTMSVariableGlobal(key)
      ? oldValue
      : emptyDatalayer[key]);
  Object.keys(prevDatalayer).forEach((key) => {
    tcVars[key] = syncFn(key, prevDatalayer[key], tcVars[key]);
  });
  window.tc_vars = {
    ...tcVars,
    ...getClientSideAttributes(),
    ...storedDatalayer,
  };
  storePreviousDatalayer(window.tc_vars);
};

/**
 * Syncs the datalayer and reloads the tagCommander container.
 * @returns {void}
 */
export const loadContainer = (tcVars: Partial<Datalayer> = {}): void => {
  const { page_name } = tcVars;
  if ((page_name && page_name === lastTrackedPage) || shouldSkipTracking()) {
    return;
  }
  syncTcVars(tcVars);
  window?.tC?.container?.reload?.({
    events: { page: [{}, {}] },
  });
  lastTrackedPage = page_name;
};

/**
 * Tracks a page view using the Tag Commander TMS (Tag Management System).
 *
 * This function handles errors in the provided data, updates the session-specific
 * filtered data layer, and optionally fetches additional base information for the
 * data layer before loading the Tag Commander container.
 *
 * @param data - The data layer object containing information to be tracked.
 * @param options - Optional configuration for the tracking operation.
 * @param options.skipFetch - If `true`, skips fetching additional base information
 *                            for the data layer. Defaults to `false`.
 * @returns A promise that resolves when the tracking operation is complete.
 */
export const trackTMSPage = async (
  data: TrackTMSPageDatalayer,
  { skipFetch }: TMSFetchOptions = {}
): Promise<void> => {
  handleTmsDataError(data);
  let tcVars: Partial<Datalayer> = { ...data };
  if (!skipFetch) {
    updateSessionFilteredDatalayer(data);
    const baseInfoDatalayer = filterDatalayer(await fetchDatalayer());
    tcVars = { ...baseInfoDatalayer, ...data };
  }
  loadContainer(tcVars);
};

/**
 * Fetches the datalayer from the backend and updates the TC vars.
 * A datalayer contains only basic/general information is needed for the TMS.
 * @returns {Promise<void>}
 */
export const fetchDatalayer = async () => {
  try {
    const response: Datalayer = await get(
      '/travel/service/tms/datalayer/general?product='
    );
    return flatten(response);
  } catch (_) {
    // avoid fetch error
    return {};
  }
};

/**
 * Fires a datalayer event with the provided category, key, and value.
 * If the key is 'customer_email', it hashes the value before updating the datalayer.
 * Synchronizes the TC variables and reloads the Tag Commander container.
 *
 * @returns {void}
 * @param data
 */
export const fireEvent = async (data: DatalayerEventPayload): Promise<void> => {
  const errors = tmsEventDataContainsErrors(data);
  if (errors) return;
  let datalayer: Partial<Datalayer> = {};
  const { customer_email } = data;
  if (customer_email) {
    const customer_email_hash = await hashValue(customer_email);
    datalayer = { customer_email_hash };
  }
  const eventData = { ...data, ...datalayer };
  Object.entries(data).forEach(([key, value]) => {
    if (key !== 'event_category' && !isNilOrEmpty(value)) {
      datalayer[key] = value;
    }
  });
  updateAndSyncDatalayer(datalayer);
  if (shouldSkipTracking()) return;
  window.tC?.event?.click?.(this, eventData);
};
